/**
 * DemoBanner — non-dismissable warning shown by /subapp/*-demo mocks.
 *
 * Mock sub-apps emit synthesized signals (Math.random / sin) so the
 * studio protocol can be exercised end-to-end before real sub-apps
 * are wired (cross-repo work, spec §1.2 #7). This banner makes that
 * unmistakable to anyone looking at the case-side window.
 *
 * Project hard rule (`feedback_no_fake_data.md`):
 *   "絕對禁止造假訊號／random／sin 模擬塞進真實功能；
 *    使用者沒說「用模擬訊號」就是不准"
 *
 * Mocks therefore only render at all on staging.home.sigmacog.xyz /
 * localhost — production hostname returns NotInProduction (see
 * isDemoHostAllowed below). The banner is the second line of defence:
 * even when the hostname check passes, the operator sees that values
 * are synthetic.
 */
import type { ReactNode } from "react";

export function isDemoHostAllowed(): boolean {
  if (typeof window === "undefined") return true;   // SSR / build
  const h = window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".localhost") ||
    h.startsWith("staging.")     // staging.home.sigmacog.xyz
  );
}

export function DemoBanner({ subapp }: { subapp: string }) {
  return (
    <div
      role="alert"
      style={{
        flexShrink: 0,
        background: "#a83323",      // var(--danger)
        color: "#f7f3ed",            // var(--paper)
        padding: "10px 24px",
        fontFamily: "var(--mono)",
        fontSize: 12,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        textAlign: "center",
        borderBottom: "2px solid #1c1814",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        zIndex: 100,
      }}
    >
      <span style={{ fontSize: 14 }}>⚠</span>
      <span>{subapp} · DEMO · 數據為合成訊號 · 非真實量測 · NOT FOR CLINICAL USE</span>
      <span style={{ fontSize: 14 }}>⚠</span>
    </div>
  );
}

export function NotInProduction({ subapp }: { subapp: string }) {
  return (
    <main
      className="page page--auth"
      style={{ paddingTop: 80, textAlign: "center" }}
    >
      <div className="serial" style={{ marginBottom: "var(--s-3)" }}>
        404 · not available in production
      </div>
      <h1>{subapp} demo 不在 production 啟用</h1>
      <p className="muted" style={{ marginTop: "var(--s-3)" }}>
        合成訊號的 mock sub-app 僅在 staging 與本機環境可見，
        正式站永不顯示假資料。請改用 staging.home.sigmacog.xyz。
      </p>
    </main>
  );
}

interface DemoGuardProps {
  subapp: string;
  children: ReactNode;
}

/** Wraps a demo sub-app: 404 outside dev/staging, banner inside. */
export function DemoGuard({ subapp, children }: DemoGuardProps) {
  if (!isDemoHostAllowed()) return <NotInProduction subapp={subapp} />;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <DemoBanner subapp={subapp} />
      <div style={{ flex: 1, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}
